import { useEffect, useRef, type ReactNode } from "react"

import { cn } from "../../lib/utils"

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/** The app's modal shell: a floating surface over the canvas that owns the
 *  behaviour a dialog has to get right, so no feature has to get it right again.
 *
 *  - `role="dialog"` + `aria-modal`, named by `label`
 *  - focus moves to the first focusable inside on open and returns to the
 *    element that opened it on close
 *  - Tab is trapped inside: without the trap the page behind the overlay stays
 *    reachable, so a keyboard user can be "inside" a modal while operating
 *    controls they cannot see
 *  - Escape and a backdrop click both close
 *
 *  The backdrop is `aria-hidden` + untabbable on purpose: it is click-to-dismiss
 *  only, so screen readers and Tab see one close affordance (the one the content
 *  renders) instead of two.
 *
 *  Content owns its own layout and supplies its own close control — this owns
 *  the shell. */
export function Modal({
  label,
  onClose,
  backdropClassName,
  shellClassName,
  surfaceClassName,
  children,
}: {
  label: string
  onClose: () => void
  backdropClassName?: string
  /** Full-viewport positioning and padding around the dialog surface. */
  shellClassName?: string
  /** Sizing and shape of the dialog surface; the shell owns only its position. */
  surfaceClassName?: string
  children: ReactNode
}) {
  const surfaceRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const previouslyFocused = globalThis.document.activeElement
    surfaceRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus()
      }
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
        return
      }
      if (event.key !== "Tab") {
        return
      }
      const focusable = surfaceRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      const first = focusable?.[0]
      const last = focusable?.[focusable.length - 1]
      if (!first || !last) {
        return
      }
      const active = globalThis.document.activeElement
      // Only the two edges need handling; Tab between them is the browser's job.
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    globalThis.document.addEventListener("keydown", onKeyDown)
    return () => globalThis.document.removeEventListener("keydown", onKeyDown)
  }, [onClose])

  return (
    <div className={cn("fixed inset-0 z-50 flex items-center justify-center p-6", shellClassName)}>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className={cn("absolute inset-0 cursor-zoom-out bg-foreground/40", backdropClassName)}
        onClick={onClose}
      />

      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cn(
          "relative z-10 flex max-h-full w-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-floating",
          surfaceClassName
        )}
      >
        {children}
      </div>
    </div>
  )
}
